import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import * as moment from 'moment';
import { cloneDeep } from 'lodash';
import { ToastrService } from 'ngx-toastr';

import { AppSessionService } from '@shared/common/session/app-session.service';
import {
    ProjectDetailsViewDto,
    NativeFieldKey,
    ProjectFieldType,
    ProjectNameMode,
    ProjectNumberMode,
    TaskServiceProxy,
    CommonLookupServiceProxy,
    SetProjectStageInputDto,
    ProjectAssociationInputDto,
} from '@shared/service-proxies/service-proxies';
import { ProjectCustomFieldsService } from '../custom-fields/project-custom-fields.service';
import { DataService } from '@app/shared/common/auth/data.service';
import { projectClientOf } from '../../shared/project-client';

/**
 * Reusable, self-contained project Details surface (custom fields + native fields) shown as a
 * slide-over from the task add/edit modals. Renders the same accordion as the view-project Details
 * tab, but without depending on that 15k-line component's internal state. Every field-value-backed
 * field is editable inline and persisted through ProjectCustomFieldsService; the native Stage uses
 * the standalone setProjectStage API. The native Client is shown read-only here (its full change
 * flow lives in the project modal's company subsystem).
 *
 * Inputs: projectId (which project to load), projectName (fallback display), clientName (native
 * client read-only display). Output: changed (any edit persisted — host should refresh its lists).
 */
@Component({
    selector: 'app-project-details-panel',
    templateUrl: './project-details-panel.component.html',
    styleUrls: ['./project-details-panel.component.scss'],
})
export class ProjectDetailsPanelComponent implements OnChanges {
    @Input() projectId: number;
    @Input() projectName: string;
    @Input() clientName: string;
    /** The tenant's word for a project ("Project" / "Matter" / "Job") — only used in copy. */
    @Input() projectLabel = 'Project';
    @Output() changed = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();
    /** The name field's open-arrow — the host takes it the rest of the way, to the project's own form. */
    @Output() openProjectForm = new EventEmitter<void>();

    get projectLabelLower(): string {
        return (this.projectLabel || 'Project').toLowerCase();
    }

    // Enum aliases for the template.
    readonly PNativeKey = NativeFieldKey;
    readonly PNameMode = ProjectNameMode;
    readonly PNumberMode = ProjectNumberMode;

    detailSections: Array<{ id: number; name: string; isGeneral: boolean; expanded: boolean; fields: any[] }> = [];
    detailsFieldsLoaded = false;
    isLoading = false;
    todayDate: any;

    // Stage (native) — loaded + persisted via the standalone project-stage API.
    allStages: any[] = [];
    selectedStage: any = null;
    private pendingStageId: any = null;

    // Custom client picker state.
    allContactsAndCompanies: any[] = [];
    customClientSearch = '';
    customClientFilter: 'all' | 'people' | 'companies' = 'all';

    // Native (project-level) client — persisted via the project-association API.
    nativeClient: any = null;
    private hasNativeClientField = false;

    // Built-from-fields name mismatch detection.
    nameMismatch = false;
    private nameMismatchSeq = 0;

    private toastrOptions = { positionClass: 'toast-bottom-right' } as any;

    constructor(
        private _customFields: ProjectCustomFieldsService,
        private _taskService: TaskServiceProxy,
        private _commonLookupServiceProxy: CommonLookupServiceProxy,
        private _appSessionService: AppSessionService,
        private toastr: ToastrService,
        private _dataService: DataService,
    ) {
        let tz = this._appSessionService.user ? this._appSessionService.user.currentTimeAsCompanyTz : undefined;
        this.todayDate = tz ? moment.parseZone(tz).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.projectId && this.projectId) {
            this.loadAll();
        }
    }

    /** Load stage config first (so the chip resolves), then the field values. */
    private loadAll(): void {
        this.detailsFieldsLoaded = false;
        this.detailSections = [];
        this.selectedStage = null;
        this.pendingStageId = null;
        this.isLoading = true;
        this._taskService.getProjectStagesConfiguration().subscribe(result => {
            if (result && !result.isError && result.responseData) {
                this.allStages = result.responseData.stages || [];
            }
            this.resolveSelectedStage();
            this.loadProjectDetailFields(this.projectId);
        }, () => {
            this.loadProjectDetailFields(this.projectId);
        });
    }

    private loadProjectDetailFields(prjId: number): void {
        if (!prjId) {
            this.isLoading = false;
            return;
        }
        this._customFields.getProjectDetails(prjId).subscribe((view: ProjectDetailsViewDto) => {
            this.isLoading = false;
            if (!view || !view.sections || view.sections.length === 0) {
                this.detailsFieldsLoaded = false;
                return;
            }
            this.buildDetailView(view);
            this.detailsFieldsLoaded = this.detailSections.length > 0;
            this.hasNativeClientField = this.detailSections.some(s => s.fields.some((f: any) => f.isNative && f.nativeKey === NativeFieldKey._3));
            this.resolveSelectedStage();
            this.loadContactsForClientFields();
            if (this.hasNativeClientField) {
                this.loadNativeClient();
            }
            this.computeNameMismatch();
        }, () => {
            this.isLoading = false;
            this.detailsFieldsLoaded = false;
        });
    }

    /** Match the native Stage field's stored id against the loaded stage list. */
    private resolveSelectedStage(): void {
        let stageField: any = null;
        this.detailSections.some(s => {
            stageField = s.fields.find((x: any) => x.isNative && x.nativeKey === NativeFieldKey._4);
            return !!stageField;
        });
        if (stageField && (stageField.raw !== undefined && stageField.raw !== null && stageField.raw !== '')) {
            this.pendingStageId = stageField.raw;
        }
        if (this.pendingStageId && this.allStages && this.allStages.length) {
            this.selectedStage = this.allStages.find(s => String(s.id) === String(this.pendingStageId)) || this.selectedStage;
        }
    }

    /** Lazy-load the contact/company lookup when there is any client field (native or custom). */
    private loadContactsForClientFields(): void {
        let hasClient = this.detailSections.some(s => s.fields.some((f: any) => f.inputKind === 'client'));
        if (!hasClient || (this.allContactsAndCompanies && this.allContactsAndCompanies.length > 0)) {
            return;
        }
        this._commonLookupServiceProxy.getAllContactsForLookup('', undefined).subscribe(result => {
            let data = (result.responseData || []).map((item: any) => {
                if (item && item.name) { item.name = String(item.name).trim(); }
                return item;
            });
            if (data && data.length > 0) {
                this.allContactsAndCompanies = cloneDeep(data);
            }
        });
    }

    // ---- view-model build ----
    private buildDetailView(view: ProjectDetailsViewDto): void {
        let sections = (view.sections || []).slice().sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
        this.detailSections = sections.map(s => {
            let fields = (s.fields || []).slice()
                .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))
                .map(f => this.toDetailFieldVm(f));
            return {
                id: s.sectionId,
                name: s.name,
                isGeneral: !!s.isGeneral,
                expanded: true,
                fields
            };
        });
    }

    private toDetailFieldVm(f: any): any {
        let kind = this.resolveDetailKind(f);
        let settings = f.settings;
        let raw = this.buildDetailRaw(f, kind, settings);
        let display = this.formatDetailValue(f, kind);
        return {
            id: f.fieldDefinitionId,
            label: f.label,
            isNative: !!f.isNative,
            nativeKey: f.nativeKey,
            fieldType: f.fieldType,
            required: !!f.isRequired,
            inputKind: kind,
            icon: this.detailFieldIcon(f),
            options: f.fieldType === ProjectFieldType._11
                ? this._customFields.locationDisplayOptions(settings)
                : (settings && settings.options ? settings.options : []),
            currency: settings && settings.defaultCurrency ? settings.defaultCurrency : 'USD',
            amountInput: kind === 'currency' ? this.formatCurrencyAmount(raw && raw.amount, (raw && raw.currency) || (settings && settings.defaultCurrency) || 'USD') : '',
            clientScope: (settings && settings.clientScope) ? settings.clientScope : 3,
            nameMode: (f.isNative && f.nativeKey === NativeFieldKey._1 && settings && settings.nameMode) ? settings.nameMode : ProjectNameMode._1,
            numberMode: (f.isNative && f.nativeKey === NativeFieldKey._2) ? ((settings && settings.numberMode) ? settings.numberMode : ProjectNumberMode._1) : ProjectNumberMode._2,
            valueJson: f.valueJson,
            raw: raw,
            editing: false,
            display: display.text,
            isHtml: display.isHtml,
            isUrl: display.isUrl,
            isEmpty: display.isEmpty,
            placeholder: 'Add ' + (f.label || '').toLowerCase()
        };
    }

    private buildDetailRaw(f: any, kind: string, settings: any): any {
        let raw = this.parseDetailJson(f.valueJson);
        switch (kind) {
            case 'currency':
                return (raw && typeof raw === 'object')
                    ? { amount: raw.amount, currency: raw.currency || (settings && settings.defaultCurrency) || 'USD' }
                    : { amount: undefined, currency: (settings && settings.defaultCurrency) || 'USD' };
            case 'toggle':
                return raw === true || raw === 'true';
            case 'daterange':
                return (raw && typeof raw === 'object') ? { start: raw.start, end: raw.end } : { start: undefined, end: undefined };
            case 'location':
                // Keep the structured address object (street/suite/city/state/zip/country) so the
                // address form can re-edit the parts; legacy values stay plain strings.
                return (raw && typeof raw === 'object') ? raw : (raw || '');
            case 'client':
                return (raw && typeof raw === 'object') ? raw : undefined;
            case 'richtext':
                return raw ? String(raw) : '';
            case 'stage':
                return (raw === undefined || raw === null) ? '' : (typeof raw === 'object' ? (raw.id || '') : raw);
            default:
                return (raw === undefined || raw === null) ? '' : (typeof raw === 'string' ? raw : String(raw));
        }
    }

    private parseDetailJson(valueJson: string): any {
        if (valueJson === undefined || valueJson === null || valueJson === '') {
            return undefined;
        }
        try {
            return JSON.parse(valueJson);
        } catch (e) {
            return valueJson;
        }
    }

    // ---- persistence ----
    private serializeDetailField(f: any): string {
        let v = f.raw;
        switch (f.inputKind) {
            case 'toggle': return JSON.stringify(!!v);
            case 'currency': return (v && v.amount !== undefined && v.amount !== null) ? JSON.stringify({ amount: v.amount, currency: v.currency || 'USD' }) : null;
            case 'location':
                if (!v) { return null; }
                if (typeof v === 'string') { return JSON.stringify({ formatted: v }); }
                return (v.formatted || v.street || v.suite || v.city || v.state || v.zip || v.country)
                    ? JSON.stringify({ formatted: v.formatted || '', street: v.street || '', suite: v.suite || '', city: v.city || '', state: v.state || '', zip: v.zip || '', country: v.country || '', isUS: v.isUS !== false })
                    : null;
            case 'daterange': return (v && (v.start || v.end)) ? JSON.stringify({ start: v.start, end: v.end }) : null;
            case 'client': return (v && v.id) ? JSON.stringify({ id: v.id, name: v.name, type: v.type }) : null;
            case 'richtext': return !this._customFields.isRichTextEmpty(v) ? JSON.stringify(String(v)) : null;
            case 'date': return v ? JSON.stringify(String(v)) : null;
            default: return (v !== undefined && v !== null && String(v).trim() !== '') ? JSON.stringify(String(v)) : null;
        }
    }

    /** Persist a single field edit, refresh its read display, and notify the host. */
    persistDetailField(f: any): void {
        let json: string = null;
        let display: any = null;
        if (!this.projectId) {
            return;
        }
        json = this.serializeDetailField(f);
        f.valueJson = json;
        display = this.formatDetailValue(f, f.inputKind);
        f.display = display.text;
        f.isHtml = display.isHtml;
        f.isUrl = display.isUrl;
        f.isEmpty = display.isEmpty;
        this._customFields.updateProjectFieldValue(this.projectId, f.id, json).subscribe(ok => {
            if (!ok) {
                this.toastr.error('Failed to save ' + f.label, null, this.toastrOptions);
            } else {
                this.changed.emit();
            }
        }, () => {
            this.toastr.error('Failed to save ' + f.label, null, this.toastrOptions);
        });
    }

    // ---- per-type edit handlers ----
    onDetailTextBlur(f: any): void {
        this.persistDetailField(f);
    }

    /** Absolute href for a stored URL value — prefixes http:// when no scheme is present
     *  (same normalization the company siteUrl save uses), so the link never resolves
     *  relative to the app. */
    detailUrlHref(v: any): string {
        let url = (v === undefined || v === null) ? '' : String(v).trim();
        if (url === '') {
            return '';
        }
        return url.toLowerCase().startsWith('http') ? url : 'http://' + url;
    }

    startDetailUrlEdit(f: any): void {
        f.editing = true;
        setTimeout(() => { $('#pdpUrlEdit' + f.id).trigger('focus'); }, 50);
    }

    finishDetailUrlEdit(f: any): void {
        f.editing = false;
        this.persistDetailField(f);
    }

    /** Address form saved — persist the structured value and refresh the comma-separated display. */
    onDetailAddressSaved(f: any, ev: any): void {
        f.raw = ev;
        f.editing = false;
        this.persistDetailField(f);
    }

    /** Location-list fields: the trailing URL of the selected value (clickable, opens a new tab). */
    locationListUrl(f: any): string {
        if (!f || f.fieldType !== ProjectFieldType._11) {
            return '';
        }
        return this._customFields.locationDisplayUrl(f.raw);
    }

    /** Selected value text shown in the dropdown trigger — the URL is rendered as its own link. */
    locationListText(f: any): string {
        if (!f) {
            return '';
        }
        if (f.fieldType !== ProjectFieldType._11) {
            return f.raw;
        }
        return this._customFields.locationDisplayText(f.raw);
    }

    selectDetailOption(f: any, opt: string): void {
        f.raw = opt;
        this.persistDetailField(f);
        $('.cf-pdp-dd .dropdown-menu').removeClass('show');
    }

    clearDetailValue(f: any): void {
        f.raw = (f.inputKind === 'daterange') ? { start: undefined, end: undefined } : undefined;
        this.persistDetailField(f);
    }

    onDetailDate(f: any, m: any): void {
        f.raw = (m && m.format) ? m.format('YYYY-MM-DD') : undefined;
        this.persistDetailField(f);
    }

    onDetailDurationApplied(f: any, ev: any): void {
        f.raw = { start: ev ? ev.startDate : undefined, end: ev ? ev.endDate : undefined };
        this.persistDetailField(f);
    }

    onDetailDurationCleared(f: any): void {
        f.raw = { start: undefined, end: undefined };
        this.persistDetailField(f);
    }

    toggleDetailBool(f: any): void {
        f.raw = !f.raw;
        this.persistDetailField(f);
    }

    startDetailRteEdit(f: any): void {
        f.editing = true;
    }

    onDetailRteChange(f: any, e: any): void {
        f.raw = e && e.value ? e.value : '';
    }

    finishDetailRteEdit(f: any): void {
        f.editing = false;
        this.persistDetailField(f);
    }

    // ---- stage (native) ----
    get openStages(): any[] {
        return (this.allStages || []).filter(s => s.isActive);
    }
    get closedStages(): any[] {
        return (this.allStages || []).filter(s => !s.isActive);
    }

    onDetailStageSelect(f: any, stage: any): void {
        let previousStage: any = null;
        let input: SetProjectStageInputDto = null;
        if (!stage || (this.selectedStage && stage.id === this.selectedStage.id) || !this.projectId) {
            $('.cf-pdp-dd .dropdown-menu').removeClass('show');
            return;
        }
        previousStage = this.selectedStage;
        this.selectedStage = stage;
        if (f) {
            f.raw = stage.id;
            f.display = stage.name;
            f.isEmpty = !stage;
        }
        input = new SetProjectStageInputDto();
        input.projectId = this.projectId;
        input.projectStageId = stage.id;
        this._taskService.setProjectStage(input).subscribe(result => {
            if (result && !result.isError) {
                this.toastr.success('Project stage updated successfully.', null, this.toastrOptions);
                this.changed.emit();
            } else {
                this.selectedStage = previousStage;
                if (f) { f.raw = previousStage ? previousStage.id : ''; f.display = previousStage ? previousStage.name : ''; f.isEmpty = !previousStage; }
                this.toastr.error((result && result.errorMessage) || 'Failed to update project stage.', null, this.toastrOptions);
            }
        }, () => {
            this.selectedStage = previousStage;
            if (f) { f.raw = previousStage ? previousStage.id : ''; f.display = previousStage ? previousStage.name : ''; f.isEmpty = !previousStage; }
            this.toastr.error('Failed to update project stage.', null, this.toastrOptions);
        });
        $('.cf-pdp-dd .dropdown-menu').removeClass('show');
    }

    // ---- currency (per-currency decimals + separators) ----
    private formatCurrencyAmount(amount: number, code: string): string {
        let dec = 0;
        if (amount === undefined || amount === null || isNaN(amount as any)) {
            return '';
        }
        dec = (code || 'USD').toUpperCase() === 'JPY' ? 0 : 2;
        return Number(amount).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
    }

    onDetailCurrencyFocus(f: any, e: any): void {
        let amt = f.raw ? f.raw.amount : undefined;
        let txt = (amt === undefined || amt === null) ? '' : String(amt);
        f.amountInput = txt;
        if (e && e.target) { e.target.value = txt; }
    }

    onDetailCurrencyInput(f: any, e: any): void {
        let el = e && e.target ? e.target : null;
        let raw: string = el ? (el.value || '') : '';
        let code = (f.raw && f.raw.currency) || f.currency || 'USD';
        let dec = code.toUpperCase() === 'JPY' ? 0 : 2;
        let parts: string[] = [];
        let dot = -1;
        raw = raw.replace(/[^0-9.]/g, '');
        parts = raw.split('.');
        if (parts.length > 2) { raw = parts[0] + '.' + parts.slice(1).join(''); }
        if (dec === 0) { raw = raw.replace(/\./g, ''); }
        else { dot = raw.indexOf('.'); if (dot >= 0 && raw.length - dot - 1 > dec) { raw = raw.substring(0, dot + 1 + dec); } }
        if (el) { el.value = raw; }
        f.amountInput = raw;
        if (!f.raw) { f.raw = { amount: undefined, currency: code }; }
        f.raw.amount = (raw === '' || raw === '.') ? undefined : Number(raw);
        if (isNaN(f.raw.amount)) { f.raw.amount = undefined; }
    }

    onDetailCurrencyBlur(f: any, e: any): void {
        let code = (f.raw && f.raw.currency) || f.currency || 'USD';
        let amt = f.raw ? f.raw.amount : undefined;
        let txt = this.formatCurrencyAmount(amt, code);
        f.amountInput = txt;
        if (e && e.target) { e.target.value = txt; }
        this.persistDetailField(f);
    }

    // ---- custom client picker ----
    getClientInitial(name: string): string {
        return (name || '').charAt(0).toUpperCase();
    }

    customClientPeople(): any[] {
        let list = (this.allContactsAndCompanies || []).filter(c => c.type === 2);
        let q = '';
        if (this.customClientSearch) {
            q = this.customClientSearch.toLowerCase();
            list = list.filter(c => c.name && c.name.toLowerCase().indexOf(q) >= 0);
        }
        return list;
    }

    customClientCompanies(): any[] {
        let list = (this.allContactsAndCompanies || []).filter(c => c.type === 1);
        let q = '';
        if (this.customClientSearch) {
            q = this.customClientSearch.toLowerCase();
            list = list.filter(c => c.name && c.name.toLowerCase().indexOf(q) >= 0);
        }
        return list;
    }

    showCustomPeople(f: any): boolean {
        return f.clientScope !== 2 && (this.customClientFilter === 'all' || this.customClientFilter === 'people');
    }

    showCustomCompanies(f: any): boolean {
        return f.clientScope !== 1 && (this.customClientFilter === 'all' || this.customClientFilter === 'companies');
    }

    setCustomClientFilter(type: 'all' | 'people' | 'companies'): void {
        this.customClientFilter = type;
    }

    customClientPlaceholder(f: any): string {
        if (f.clientScope === 1) { return 'Select person'; }
        if (f.clientScope === 2) { return 'Select company'; }
        return 'Select client';
    }

    selectCustomClient(f: any, item: any): void {
        f.raw = item;
        this.persistDetailField(f);
        this.customClientSearch = '';
        $('.cf-pdp-client-drop .dropdown-menu').removeClass('show');
    }

    clearCustomClient(f: any): void {
        f.raw = undefined;
        this.persistDetailField(f);
        $('.cf-pdp-client-drop .dropdown-menu').removeClass('show');
    }

    /**
     * Open the picked custom-client's own details screen from the field's arrow (parity with the
     * native client / Details-tab project field). f.raw.type: 1 = company, 2 = person.
     */
    openCustomClientDetails(f: any): void {
        const client = f ? f.raw : null;
        if (!client || !client.id) {
            return;
        }
        if (client.type === 1) {
            this._dataService.openEditCompanyModal.next({
                companyId: Number(client.id),
                companyName: client.name,
                keepTaskOpen: true,
            });
        } else if (client.type === 2) {
            this._dataService.openEditContactModal.next({
                id: Number(client.id),
                keepTaskOpen: true,
            });
        }
    }

    // ---- native (project-level) client: persisted via the project-association API ----
    /** Load the project's current primary client (company assoc type 2 / contact assoc type 3). */
    private loadNativeClient(): void {
        if (!this.projectId) {
            return;
        }
        this._taskService.getProjectAssociations(this.projectId, undefined).subscribe(result => {
            // The same pick as the server's Client event (lowest row id of the type 2/3 rows), so this panel and the
            // other open screens agree on a project that carries more than one client row.
            const assoc = result && !result.isError ? projectClientOf(result.responseData) : null;
            if (assoc) {
                this.nativeClient = { id: assoc.associationId, proAssoId: assoc.proAssoId, type: assoc.associationType === 3 ? 2 : 1, name: (assoc.associationName || '').trim() };
                this.clientName = this.nativeClient.name;
            } else {
                this.nativeClient = null;
            }
            if (this.detailsFieldsLoaded) {
                this.computeNameMismatch();
            }
        });
    }

    /** People shown in the native client picker (filter-aware). */
    nativeClientPeople(): any[] {
        if (this.customClientFilter === 'companies') { return []; }
        return this.customClientPeople();
    }

    /** Companies shown in the native client picker (filter-aware). */
    nativeClientCompanies(): any[] {
        if (this.customClientFilter === 'people') { return []; }
        return this.customClientCompanies();
    }

    selectNativeClient(item: any): void {
        let input: ProjectAssociationInputDto = null;
        if (!item || !this.projectId) {
            return;
        }
        this.customClientSearch = '';
        $('.cf-pdp-native-client-drop .dropdown-menu').removeClass('show');
        input = new ProjectAssociationInputDto();
        input.projectId = this.projectId;
        input.associationType = item.type === 2 ? 3 : 2;
        input.associationIds = [item.id];
        this._taskService.createUpdateProjectAssociation(input).subscribe(result => {
            // The response lists every association, contacts included and in no set order: [0] can be a contact.
            const a = result && !result.isError ? projectClientOf(result.responseData) : null;
            if (a) {
                this.nativeClient = { id: a.associationId, proAssoId: a.proAssoId, type: a.associationType === 3 ? 2 : 1, name: (a.associationName || '').trim() };
                this.clientName = this.nativeClient.name;
                this.changed.emit();
                this.computeNameMismatch();
                this.toastr.success('Client updated.', null, this.toastrOptions);
            } else {
                this.toastr.error('Failed to update client.', null, this.toastrOptions);
            }
        }, () => {
            this.toastr.error('Failed to update client.', null, this.toastrOptions);
        });
    }

    clearNativeClient(): void {
        let proAssoId: any = this.nativeClient ? this.nativeClient.proAssoId : null;
        // Clear is now triggered from inside the open dropdown (hover the selected row) — close it,
        // matching the Details-tab project field's clear behavior.
        $('.cf-pdp-native-client-drop .dropdown-menu').removeClass('show');
        if (!proAssoId) {
            this.nativeClient = null;
            this.clientName = '';
            return;
        }
        this._taskService.deleteProjectAssociation([proAssoId], undefined, undefined, undefined).subscribe(result => {
            if (result && !result.isError) {
                this.nativeClient = null;
                this.clientName = '';
                this.changed.emit();
                this.computeNameMismatch();
                this.toastr.success('Client cleared.', null, this.toastrOptions);
                // A project can carry another client row (legacy duplicates): show it, with its row id, if one is left.
                this.loadNativeClient();
            } else {
                this.toastr.error('Failed to clear client.', null, this.toastrOptions);
            }
        }, () => {
            this.toastr.error('Failed to clear client.', null, this.toastrOptions);
        });
    }

    /**
     * Open the client's own details screen from the Client field's arrow (parity with the Details-tab
     * project field's "open project details" arrow). nativeClient.type: 1 = company, 2 = person.
     */
    openNativeClientDetails(): void {
        if (!this.nativeClient || !this.nativeClient.id) {
            return;
        }
        if (this.nativeClient.type === 1) {
            this._dataService.openEditCompanyModal.next({
                companyId: Number(this.nativeClient.id),
                companyName: this.nativeClient.name,
                keepTaskOpen: true,
            });
        } else if (this.nativeClient.type === 2) {
            this._dataService.openEditContactModal.next({
                id: Number(this.nativeClient.id),
                keepTaskOpen: true,
            });
        }
    }

    // ---- built-from-fields name: on-demand rebuild ----
    private collectNameSourceValues(): { values: any[]; nativeValues: any[] } {
        let values: any[] = [];
        let nativeValues: any[] = [];
        this.detailSections.forEach(s => {
            s.fields.forEach((f: any) => {
                if (!f.isNative && f.valueJson) {
                    values.push({ fieldDefinitionId: f.id, valueJson: f.valueJson });
                } else if (f.isNative) {
                    if (f.nativeKey === NativeFieldKey._2 && f.raw) {
                        nativeValues.push({ nativeKey: f.nativeKey, displayValue: String(f.raw) });
                    } else if (f.nativeKey === NativeFieldKey._3 && this.clientName) {
                        nativeValues.push({ nativeKey: f.nativeKey, displayValue: this.clientName });
                    } else if (f.nativeKey === NativeFieldKey._4 && this.selectedStage) {
                        nativeValues.push({ nativeKey: f.nativeKey, displayValue: this.selectedStage.name });
                    }
                }
            });
        });
        return { values: values, nativeValues: nativeValues };
    }

    private computeNameMismatch(): void {
        let nameField: any = null;
        let src: any = null;
        let seq = 0;
        this.nameMismatch = false;
        this.detailSections.some(s => {
            nameField = s.fields.find((x: any) => x.isNative && x.nativeKey === NativeFieldKey._1);
            return !!nameField;
        });
        if (!nameField || nameField.nameMode !== ProjectNameMode._2) {
            return;
        }
        seq = ++this.nameMismatchSeq;
        src = this.collectNameSourceValues();
        this._customFields.buildProjectNamePreview(ProjectNameMode._2, src.values, src.nativeValues).subscribe(built => {
            if (seq !== this.nameMismatchSeq) {
                return;
            }
            let current = (nameField.raw === undefined || nameField.raw === null) ? '' : String(nameField.raw).trim();
            this.nameMismatch = !!(built && built.trim() !== '' && built.trim() !== current);
        }, () => { /* best-effort */ });
    }

    rebuildProjectName(f: any): void {
        let src = this.collectNameSourceValues();
        this._customFields.buildProjectNamePreview(ProjectNameMode._2, src.values, src.nativeValues).subscribe(name => {
            if (name && name.trim() !== '') {
                f.raw = name;
                this.persistDetailField(f);
                this.nameMismatch = false;
                this.toastr.success('Project name rebuilt from fields.', null, this.toastrOptions);
            }
        }, () => {
            this.toastr.error('Failed to rebuild project name.', null, this.toastrOptions);
        });
    }

    assignProjectNumber(f: any): void {
        if (!this.projectId) {
            return;
        }
        this._customFields.assignAutoProjectNumber(this.projectId).subscribe(num => {
            if (num) {
                f.raw = num;
                f.valueJson = JSON.stringify(num);
                f.display = num;
                f.isEmpty = false;
                this.changed.emit();
                this.toastr.success('Project number assigned.', null, this.toastrOptions);
            } else {
                this.toastr.error('Failed to assign project number.', null, this.toastrOptions);
            }
        }, () => {
            this.toastr.error('Failed to assign project number.', null, this.toastrOptions);
        });
    }

    // ---- formatting / icons ----
    private resolveDetailKind(f: any): string {
        if (f.isNative) {
            switch (f.nativeKey) {
                case NativeFieldKey._1: return 'name';
                case NativeFieldKey._2: return 'number';
                case NativeFieldKey._3: return 'client';
                case NativeFieldKey._4: return 'stage';
                case NativeFieldKey._5: return 'daterange';
                case NativeFieldKey._6: return 'currency';
                case NativeFieldKey._7: return 'location';
                case NativeFieldKey._8: return 'richtext';
                case NativeFieldKey._9: return 'toggle';
                default: return 'text';
            }
        }
        switch (f.fieldType) {
            case ProjectFieldType._2: return 'dropdown';
            case ProjectFieldType._3: return 'date';
            case ProjectFieldType._4: return 'client';
            case ProjectFieldType._5: return 'url';
            case ProjectFieldType._6: return 'currency';
            case ProjectFieldType._7: return 'location';
            case ProjectFieldType._8: return 'richtext';
            case ProjectFieldType._9: return 'daterange';
            case ProjectFieldType._10: return 'toggle';
            case ProjectFieldType._11: return 'dropdown'; // location list = dropdown of curated addresses
            default: return 'text';
        }
    }

    private formatDetailValue(f: any, kind: string): { text: string; isHtml: boolean; isUrl: boolean; isEmpty: boolean } {
        let raw = this.parseDetailJson(f.valueJson);
        let empty = { text: '', isHtml: false, isUrl: false, isEmpty: true };
        let name = '';
        let on = false;
        let code = '';
        let dec = 0;
        let m: any = null;
        let s: any = null;
        let e: any = null;
        let st = '';
        let en = '';
        let text = '';

        // Native Client + Stage resolve from inputs / the loaded stage (backend value is null / id).
        if (f.isNative && f.nativeKey === NativeFieldKey._3) {
            name = this.getNativeClientName();
            return name ? { text: name, isHtml: false, isUrl: false, isEmpty: false } : empty;
        }
        if (f.isNative && f.nativeKey === NativeFieldKey._4) {
            name = this.selectedStage ? this.selectedStage.name : '';
            return name ? { text: name, isHtml: false, isUrl: false, isEmpty: false } : empty;
        }

        switch (kind) {
            case 'richtext':
                return !this._customFields.isRichTextEmpty(raw) ? { text: String(raw), isHtml: true, isUrl: false, isEmpty: false } : empty;
            case 'toggle':
                if (raw === undefined || raw === null) { return empty; }
                on = raw === true || raw === 'true';
                text = (f.isNative && f.nativeKey === NativeFieldKey._9) ? (on ? 'Private' : 'Public') : (on ? 'Yes' : 'No');
                return { text: text, isHtml: false, isUrl: false, isEmpty: false };
            case 'url':
                return raw ? { text: String(raw), isHtml: false, isUrl: true, isEmpty: false } : empty;
            case 'currency':
                if (!raw || raw.amount === undefined || raw.amount === null) { return empty; }
                code = raw.currency || 'USD';
                dec = code.toUpperCase() === 'JPY' ? 0 : 2;
                return { text: this.detailCurrencySymbol(code) + Number(raw.amount).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }), isHtml: false, isUrl: false, isEmpty: false };
            case 'location':
                text = raw ? (typeof raw === 'string' ? raw : (raw.formatted || '')) : '';
                return text ? { text: text, isHtml: false, isUrl: false, isEmpty: false } : empty;
            case 'date':
                if (!raw) { return empty; }
                m = moment(raw);
                return m.isValid() ? { text: m.format('MMM D, YYYY'), isHtml: false, isUrl: false, isEmpty: false } : { text: String(raw), isHtml: false, isUrl: false, isEmpty: false };
            case 'daterange':
                if (!raw || (!raw.start && !raw.end)) { return empty; }
                s = raw.start ? moment(raw.start) : null;
                e = raw.end ? moment(raw.end) : null;
                st = (s && s.isValid()) ? s.format('MMM D') : '';
                en = (e && e.isValid()) ? e.format('MMM D, YYYY') : '';
                text = (st && en) ? (st + ' → ' + en) : (st || en);
                return text ? { text: text, isHtml: false, isUrl: false, isEmpty: false } : empty;
            case 'client':
                name = raw ? (typeof raw === 'string' ? raw : raw.name) : '';
                return name ? { text: name, isHtml: false, isUrl: false, isEmpty: false } : empty;
            default:
                text = (raw === undefined || raw === null) ? '' : (typeof raw === 'string' ? raw : String(raw));
                return text !== '' ? { text: text, isHtml: false, isUrl: false, isEmpty: false } : empty;
        }
    }

    private detailFieldIcon(f: any): string {
        if (f.isNative) {
            switch (f.nativeKey) {
                case NativeFieldKey._1: return 'briefcase';
                case NativeFieldKey._2: return 'hash';
                case NativeFieldKey._3: return 'users';
                case NativeFieldKey._4: return 'flag';
                case NativeFieldKey._5: return 'calendar';
                case NativeFieldKey._6: return 'dollar-sign';
                case NativeFieldKey._7: return 'map-pin';
                case NativeFieldKey._8: return 'align-left';
                case NativeFieldKey._9: return 'lock';
                default: return 'type';
            }
        }
        return this._customFields.typeIcon(f.fieldType);
    }

    private detailCurrencySymbol(code: string): string {
        switch ((code || 'USD').toUpperCase()) {
            case 'EUR': return '€';
            case 'GBP': return '£';
            case 'JPY': return '¥';
            default: return '$';
        }
    }

    detailCurrencySymbolFor(f: any): string {
        return this.detailCurrencySymbol((f.raw && f.raw.currency) || f.currency || 'USD');
    }

    /** Native client name — the loaded project association, falling back to the host-provided name. */
    private getNativeClientName(): string {
        if (this.nativeClient && this.nativeClient.name) { return this.nativeClient.name; }
        if (this.clientName) { return this.clientName; }
        return '';
    }

    nativeClientDisplay(f: any): string {
        return this.getNativeClientName();
    }

    toggleDetailSection(s: any): void {
        s.expanded = !s.expanded;
    }

    trackByDetailSection(index: number, s: any): any { return s.id; }
    trackByDetailField(index: number, f: any): any { return f.id; }

    onClose(): void {
        this.closed.emit();
    }
}
